import { useMemo } from 'react';
import { setPendingVisible, setPinnedFromIndex } from '../store/slice/config/config';
import { isGeoColumn } from '../util/table-helper';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';

/**
 * useGeoSummaryDropdown
 * ----------------------------------------------------
 * Manages temporary geo-level selection for the
 * "Sum By Geo" dropdown. Fully independent from the
 * client/company/region/group/location filter cascade -
 * that cascade only controls which filter OPTIONS are
 * available, it has no bearing on Sum By.
 *
 * Responsibilities:
 * - Tracks temporary geo column visibility using pendingVisible
 * - Prevents unchecking the last remaining checked column
 * - Supports grouped geo column pinning
 *
 * Key concepts:
 * - checkedColumns: geo columns currently marked as visible
 *   (pendingVisible if present, otherwise persisted visibility)
 * - pendingVisible: temporary visibility state, not applied to
 *   the table until the user confirms via Sum By / Search / Update data
 * - groupBy (statistics aggregation) is derived elsewhere
 *   (resolveGroupByFromColumns) from the DEEPEST checked column here
 *
 * This hook guarantees:
 * - No local state duplication
 * - Redux remains the single source of truth
 * - No side effects during render
 */

export const useGeoSummaryDropdown = () => {
  const dispatch = useAppDispatch();
  const pageKey = useAppSelector((state) => state.configSlice.activePageKey);
  const reduxColumns = useAppSelector((state) => state.configSlice.columnsByPage[pageKey]);

  const columns = useMemo(() => reduxColumns?.filter((c) => isGeoColumn(c.name)) ?? [], [reduxColumns]);

  /**
   * Currently checked geo columns.
   * Derived from pendingVisible (uncommitted UI change) if present,
   * otherwise from the persisted isVisible.
   */
  const checkedColumns = useMemo(
    () => columns.filter((c) => (c.pendingVisible ?? c.isVisible) !== false).map((c) => c.name),
    [columns],
  );

  /**
   * Toggles temporary visibility of a geo column (checkbox click).
   *
   * Rule: at least one geo column must stay checked at all times -
   * unchecking the only remaining checked column is a no-op.
   *
   * This only sets pendingVisible - the table's actual column
   * visibility (and the resulting groupBy) is applied later,
   * when the user confirms via Sum By / Search / Update data.
   */
  const handleToggleColumn = (column: string) => {
    const isCurrentlyChecked = checkedColumns.includes(column);
    const isLastChecked = isCurrentlyChecked && checkedColumns.length === 1;

    if (isLastChecked) return;

    dispatch(setPendingVisible({ pageKey, columnName: column, visible: !isCurrentlyChecked }));
  };

  /**
   * Toggles pinning for a geo column. Handled entirely by the
   * Redux slice, which keeps pinning contiguous across the
   * geo column hierarchy.
   */
  const handlePinToggle = (columnName: string) => {
    dispatch(setPinnedFromIndex({ pageKey, columnName }));
  };

  return {
    columns,
    checkedColumns,
    handleToggleColumn,
    handlePinToggle,
  };
};
